import { useEffect, useState } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { shareSocialOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { useCreateShareLink } from '../../hooks/useListings';
import { logShareFailure } from '../../utils/shareLog';

interface ShareQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Route encoded into the share link + QR. Defaults to '/' (a generic app
   * share). Pass a different in-app route to attribute the scan to a specific
   * destination.
   */
  route?: string;
  /** Brand color the backend renders the QR in. Defaults to 'primary'. */
  qrColor?: 'primary' | 'secondary';
  /**
   * z-index utility class for the underlying Modal layer. Defaults to the
   * Modal's own default (`z-[60]`). Raise it when opening from above a
   * high-stacking surface — e.g. an open IonMenu drawer sits at z-index 1000,
   * so the sidebar share button passes a value above that.
   */
  zIndexClass?: string;
}

/**
 * Reusable centered popup showing the caller's personal app-share QR plus a
 * Share button (native OS share sheet, clipboard fallback). Requires an
 * authenticated session — the QR endpoint (`POST /share?qr`) is user-scoped,
 * so only render this for logged-in users. Built on the generic {@link Modal}
 * (portals to body, backdrop, centered) rather than a bottom sheet so it drops
 * into any surface. Self-contained: fetches its own share code on open.
 */
export const ShareQRModal: React.FC<ShareQRModalProps> = ({
  isOpen,
  onClose,
  route = '/',
  qrColor = 'primary',
  zIndexClass,
}) => {
  const { t } = useTranslation('common');
  const shareMutation = useCreateShareLink();
  const [qrData, setQrData] = useState<{ ref: string; qrUrl?: string } | null>(null);
  const [qrFailed, setQrFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch the caller's app-share code + QR image whenever the modal opens.
  // Re-runs on each open so the link's expiry is refreshed; the backend returns
  // the same cached QR when nothing changed, so it's cheap after the first
  // render. `cancelled` guards against a late resolve writing to an unmounted /
  // already-closed modal.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setQrData(null);
    setQrFailed(false);
    shareMutation
      .mutateAsync({ route, qr: qrColor })
      .then((res) => {
        if (!cancelled) setQrData(res);
      })
      .catch(() => {
        if (!cancelled) setQrFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // shareMutation identity is stable across renders; re-fetch only on
    // open/route/color change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, route, qrColor]);

  // Reuse the ref already minted for the QR on open. The Share button is
  // disabled until `qrData` lands (see `disabled` below), so this runs with the
  // ref already in hand — no await before navigator.share(). That's what keeps
  // it working on Safari, which only opens the native share sheet when the call
  // happens synchronously inside the tap's transient user activation; awaiting a
  // mint first drops the activation and the share fails silently.
  const performShare = async () => {
    if (!qrData) return;
    try {
      const shareUrl = `${window.location.origin}/share?ref=${qrData.ref}`;
      if (navigator.share) {
        await navigator.share({ title: t('share.shareTitle'), url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      // Silent on user-cancel (AbortError); logs real share/clipboard failures.
      logShareFailure(err, 'app-share-qr');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      zIndexClass={zIndexClass}
      className="flex flex-col items-center text-center gap-5"
    >
      <div>
        <p className="text-base font-semibold text-foreground">{t('share.qrHeading')}</p>
      </div>

      {/* Square plate that fills the card width but caps at 18rem, so it stays
          large on phones without overflowing the card (a fixed w-72 was wider
          than the card's inner width on narrow viewports and caused horizontal
          scroll). aspect-square keeps height locked to width so the popup
          doesn't resize between loading and loaded states. White backing (not a
          theme surface) is deliberate: the QR must keep a light quiet-zone to
          stay scannable in dark mode. */}
      <div className="flex aspect-square w-full max-w-[18rem] items-center justify-center rounded-2xl border border-border bg-white p-3">
        {qrData?.qrUrl ? (
          <img
            src={qrData.qrUrl}
            alt={t('share.qrHeading')}
            className="h-full w-full object-contain"
          />
        ) : qrFailed ? (
          <p className="px-4 text-sm text-muted-foreground">{t('share.qrError')}</p>
        ) : (
          <IonSpinner name="crescent" />
        )}
      </div>

      <button
        type="button"
        onClick={() => void performShare()}
        disabled={!qrData}
        className="flex items-center justify-center gap-1.5 rounded-full bg-[var(--ion-color-primary)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm active:scale-[0.99] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
      >
        <IonIcon icon={shareSocialOutline} className="text-base" />
        {copied ? t('toast.copied') : t('actions.share')}
      </button>
    </Modal>
  );
};
